import { Avatar, Badge, Box, Grid, IconButton, RadioGroup, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { UserSettings } from 'types/userSettings';

import { i18n } from 'lib/i18n';

import FamilyDialog from 'components/shared/Dialog/FamilyDialog';

import FormControlLabel from 'components/shared/FormControlLabel/FormControlLabel';
import Radio from 'components/shared/Radio/Radio';

import MemberAvatar from 'components/User/MemberAvatar';

import useFamilyMembers from './hooks/useFamilyMembers';

import { useColors } from 'hooks/useColors';

import {
	addFamilyMemberCss,
	avatarNameCss,
	avatarRadioBtn,
	boxFullNameCss,
	customIconCheckCss,
	customIconCss,
	customMainIconCss,
	familyMembersContainerCss,
	radioGroupCss,
	userAvatarSpaceCss,
} from 'components/Checkout/styles';
import { userStore } from 'stores/userStore';
import { useRecoilValue } from 'recoil';
import { useEffect } from 'react';

export interface Props {
	updateSelectedMember: (member: UserSettings | null) => void;
}

const FamilyMembers = ({ updateSelectedMember }: Props) => {
	const userSettings = useRecoilValue(userStore.userSettings);
	const {
		openFamilyDialog,
		setOpenFamilyDialog,
		selectedMember,
		setSelectedMember,
		setFamilyList,
		familyList,
		familyAccount,
		handleFamilyDialogClose,
		handleFamilyDialogCloseEvent,
		refreshFamilyList,
	} = useFamilyMembers();

	const { colors } = useColors();

	const handleChange = (
		_event: React.ChangeEvent<HTMLInputElement>,
		// Value of the selected radio button. The DOM API casts it to a string.
		value: string
	) => {
		const memberId = Number(value);
		const memberData = familyList?.find(({ id }) => id === memberId) ?? (userSettings as UserSettings);

		setSelectedMember(memberData ?? null);
		updateSelectedMember(memberData ?? null);
	};

	useEffect(() => {
		if (familyAccount) {
			refreshFamilyList(familyAccount);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!familyList && userSettings) {
			setFamilyList([userSettings]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [familyList]);

	return (
		<>
			<Grid container css={familyMembersContainerCss}>
				<RadioGroup
					css={radioGroupCss}
					row
					aria-label="position"
					name="position"
					defaultValue="top"
					value={(selectedMember as UserSettings)?.id ?? null}
					onChange={handleChange}
				>
					{familyList?.map((member: UserSettings) => (
						<Grid key={member.id} item xs={4} sm={6} css={userAvatarSpaceCss}>
							<Grid container justify="center">
								<Badge
									overlap="circle"
									anchorOrigin={{
										vertical: 'top',
										horizontal: 'left',
									}}
									badgeContent={
										<FormControlLabel
											css={avatarRadioBtn}
											value={member.id}
											control={
												<Radio
													color="secondary"
													icon={
														<div css={customMainIconCss(colors)}>
															<div css={customIconCss(colors)} />
														</div>
													}
													checkedIcon={
														<div css={customIconCheckCss(colors)}>
															<CheckCircleIcon />
														</div>
													}
												/>
											}
											label={<MemberAvatar member={member} />}
										/>
									}
								/>
							</Grid>

							<Grid container justify="center">
								<Box pt={9} css={boxFullNameCss}>
									<Typography variant="h5" css={avatarNameCss(colors)}>
										{member.firstName} {member.lastName}
									</Typography>
								</Box>
							</Grid>
						</Grid>
					))}
					<Grid item xs={4} sm={6} css={addFamilyMemberCss(colors)}>
						<Grid container justify="center">
							<Badge
								overlap="circle"
								anchorOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
								badgeContent={
									<IconButton onClick={() => setOpenFamilyDialog(true)}>
										<Avatar>
											<AddIcon />
										</Avatar>
									</IconButton>
								}
							/>
						</Grid>
						<Grid container justify="center">
							<Box pt={9} css={boxFullNameCss}>
								<Typography variant="h5" css={avatarNameCss(colors)}>
									{i18n.user.family.addAFamilyMember}
								</Typography>
							</Box>
						</Grid>
					</Grid>
				</RadioGroup>
			</Grid>

			{/* Popup Dialog */}
			<FamilyDialog
				open={openFamilyDialog}
				onCloseEvent={handleFamilyDialogCloseEvent}
				onClose={() => handleFamilyDialogClose(familyAccount || undefined)}
				familyId={familyAccount?.[0]?.id}
				aria-labelledby="form-dialog-title"
				maxWidth="md"
				fullWidth
			/>
		</>
	);
};

export default FamilyMembers;
